
import {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import DatePicker from "react-datepicker";
import moment from "moment";
import toast from "react-hot-toast";

import "./PatientConsent.css";

import SignaturePad from "../../../Component/shared/signaturePad";
import SpinnerLoader from "../../../Component/shared/loader/spinnerLoader/SpinnerLoader";
import { GragIntakeSheetData } from "../IntakeSheetUtils";
import { IntakeSheetSession, PatientConsent } from "../../../helpers/constant";
import { GetCaseFormListData, SaveCaseFormData } from "../Service";

// ─── helpers ──────────────────────────────────────────────────────────────────

const SHARED_SIG_KEY = "sharedPatientSignature";

function setInCollection(collection, name, value) {
  const item = collection.find((x) => x.Name === name);
  if (item) item.Value = value;
}

// ─── component ────────────────────────────────────────────────────────────────

const PatientConsentForm = forwardRef(function PatientConsentForm(props, ref) {
  const { signatureMiss, copySignature } = props;

  // ── session ───────────────────────────────────────────────────────────────
  const tokenData   = JSON.parse(sessionStorage.getItem("tokenData") || "{}");
  const SessionData = JSON.parse(sessionStorage.getItem("sessionData") || "{}");
  const doctorName  = SessionData?.Physician?.Name ?? "";
  const doctorId    = SessionData?.Physician?.ID   ?? "";

  // ── state ─────────────────────────────────────────────────────────────────
  const [loading,                     setLoading]                     = useState(false);
  const [collectionOfPatientConsent,  setCollectionOfPatientConsent]  = useState([]);
  const [beforeParsingPatientConsent, setBeforeParsingPatientConsent] = useState({});
  const [caseFormDataId,              setCaseFormDataId]              = useState("");

  const [patientName,           setPatientName]           = useState("");
  const [patientSignImg,        setPatientSignImg]         = useState("");
  const [patientSignDate,       setPatientSignDate]        = useState(new Date());
  const [relationShipToPatient, setRelationShipToPatient]  = useState("");
  const [witness,               setWitness]                = useState("");
  const [sigPadOpen,            setSigPadOpen]             = useState(false);

  // ── refs: always hold the latest values — solves stale closure in save ───
  const latestRef = useRef({});
  useEffect(() => {
    latestRef.current = {
      collectionOfPatientConsent,
      beforeParsingPatientConsent,
      caseFormDataId,
      patientName,
      patientSignImg,
      patientSignDate,
      relationShipToPatient,
      witness,
    };
  }, [
    collectionOfPatientConsent,
    beforeParsingPatientConsent,
    caseFormDataId,
    patientName,
    patientSignImg,
    patientSignDate,
    relationShipToPatient,
    witness,
  ]);

  // ── imperative handle ─────────────────────────────────────────────────────
  useImperativeHandle(ref, () => ({
    save:           () => saveFunction(),
    isPending:      () => !latestRef.current.patientSignImg,
    getSignature:   () => latestRef.current.patientSignImg || "",
    applySignature: (sig) => {
      if (!sig) return;
      setPatientSignImg(sig);
      setPatientSignDate(new Date());
    },
  }));

  useEffect(() => {
    props?.onReady?.("patient_consent");
  }, [patientSignImg]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    callApi();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── GET ───────────────────────────────────────────────────────────────────
  const handleGetCaseFormData = async () => {
    try {
      const requestBody = {
        CaseFormDataId:  null,
        CaseId:          tokenData.caseId,
        CompanyId:       tokenData.companyId,
        DoctorId:        "ALL",
        EntityLevel:     0,
        FormId:          15,
        FormName:        "Patient Consent",
        FormType:        null,
        IsFormCompleted: false,
        JsonData:        null,
        TemplateName:    null,
        UserId:          "US000000000000012172",
      };
      const response = await GetCaseFormListData(requestBody);
      if (response?.HasException) {
        toast.error(`Something went wrong. ${response?.MessageText}. ${response?.Exception}`);
        return {};
      }
      return response?.Data?.[0] || {};
    } catch (error) {
      toast.error(error.message);
      return {};
    }
  };

  const callApi = async () => {
    setLoading(true);
    const formData = await handleGetCaseFormData();

    if (!formData?.JsonData) {
      signatureMiss?.(true);
      setLoading(false);
      return;
    }

    const patientConsentData = JSON.parse(formData.JsonData);
    const collection         = GragIntakeSheetData(patientConsentData);

    setCollectionOfPatientConsent(collection);
    setBeforeParsingPatientConsent(patientConsentData);
    setCaseFormDataId(formData.CaseFormDataId || "");

    bindValueToControl(collection);
    setLoading(false);
  };

  // ── bind values ───────────────────────────────────────────────────────────
  const bindValueToControl = (collection) => {
    const get = (key) => {
      const val = collection.find((x) => x.Name === key)?.Value ?? "";
      return val === "01/01/1900" ? "" : val;
    };

    const normalise = (raw) =>
      !raw
        ? ""
        : raw.includes(IntakeSheetSession.dataBlock)
          ? raw
          : IntakeSheetSession.dataBlock + raw;

    const parseDateSafe = (raw) =>
      isNaN(new Date(raw).getTime()) ? new Date() : new Date(raw);

    const savedSig    = copySignature ? sessionStorage.getItem(SHARED_SIG_KEY) : null;
    const resolvedSig = savedSig || normalise(get(PatientConsent.sigConsentGiverSign));

    setPatientName(get(PatientConsent.txtConsentGiverName));
    setPatientSignImg(resolvedSig);
    setPatientSignDate(parseDateSafe(get(PatientConsent.txtConsentGiverSignDate)));
    setRelationShipToPatient(get(PatientConsent.txtRelationWithPatient));
    setWitness(get(PatientConsent.txtWitnessName));

    if (!resolvedSig) signatureMiss?.(true);
  };

  // ── update collection — reads from latestRef ─────────────────────────────
  const updateCollection = (collection, latest) => {
    const updated = [...collection];
    const fmt = (d) =>
      d == null || d === ""
        ? ""
        : moment(d).format("MM-DD-YYYY").split("-").join("/");

    setInCollection(updated, PatientConsent.txtConsentGiverName,     latest.patientName);
    setInCollection(updated, PatientConsent.sigConsentGiverSign,     latest.patientSignImg);
    setInCollection(updated, PatientConsent.txtConsentGiverSignDate, fmt(latest.patientSignDate));
    setInCollection(updated, PatientConsent.txtRelationWithPatient,  latest.relationShipToPatient);
    setInCollection(updated, PatientConsent.txtWitnessName,          latest.witness);
    setInCollection(updated, PatientConsent.txtPhysicianName,        doctorName);
    setInCollection(updated, PatientConsent.ddlPhysicianList,        doctorId);

    return updated;
  };

  // ── SAVE API ──────────────────────────────────────────────────────────────
  const callSaveApi = async (jsonData, currentCaseFormDataId) => {
    try {
      const payload = {
        CaseFormDataId:  currentCaseFormDataId || null,
        CaseId:          tokenData.caseId,
        CompanyId:       tokenData.companyId,
        DoctorId:        tokenData.doctorId || tokenData.DoctorId || "",
        EntityLevel:     0,
        FormId:          15,
        FormName:        "Patient Consent",
        FormType:        null,
        IsChanged:       false,
        IsCopySign:      !!copySignature,
        IsFormCompleted: false,
        TemplateName:    null,
        UserId:          tokenData?.userId,
        officeId:        tokenData?.providerId,
        JsonData:        jsonData,
      };

      const response = await SaveCaseFormData(payload);

      if (response?.HasException) {
        toast.error(`Error while saving Patient Consent. ${response?.MessageText || ""}`);
        return false;
      }
      if (response?.Data?.[0]?.CaseFormDataId) {
        setCaseFormDataId(response.Data[0].CaseFormDataId);
      }
      toast.success("Patient Consent saved successfully.");
      return true;
    } catch (err) {
      toast.error("Error while saving Patient Consent data.");
      return false;
    }
  };

  // ── formJsonRequest ───────────────────────────────────────────────────────
  const formJsonRequest = async (updatedCollection, beforeData, currentCaseFormDataId) => {
    try {
      for (let i = 0; i < beforeData.notesdata.length; i++) {
        const filterArr = updatedCollection.filter(
          (x) => x.RenderKeyName === beforeData.notesdata[i].RenderKeyName,
        );
        if (!filterArr.length) continue;

        for (let j = 0; j < beforeData.notesdata[i].ApplicationValue.length; j++) {
          if (beforeData.notesdata[i].ApplicationValue[j].Name !== filterArr[j]?.Name) continue;

          beforeData.notesdata[i].ApplicationValue[j].Type     = filterArr[j].Type;
          beforeData.notesdata[i].ApplicationValue[j].isRender = filterArr[j].isRender;

          const isSign =
            beforeData.notesdata[i].ApplicationValue[j].Name === PatientConsent.sigConsentGiverSign;

          if (isSign) {
            const val = filterArr[j].Value;
            beforeData.notesdata[i].ApplicationValue[j].Value = val
              ? val.includes(";") ? val.split(";")[1].split(",")[1] : val
              : "";
          } else {
            beforeData.notesdata[i].ApplicationValue[j].Value = filterArr[j].Value;
          }
        }
      }

      const result = await callSaveApi(JSON.stringify(beforeData), currentCaseFormDataId);
      setCollectionOfPatientConsent(updatedCollection);
      setBeforeParsingPatientConsent(beforeData);
      return result;
    } catch (err) {
      console.error("formJsonRequest error:", err?.message);
      return false;
    }
  };

  // ── saveFunction — reads from latestRef, never stale ─────────────────────
  const saveFunction = async () => {
    // Pull the very latest values from the ref (bypasses stale closure)
    const latest = latestRef.current;

    if (!latest.collectionOfPatientConsent?.length || !latest.beforeParsingPatientConsent?.notesdata) {
      toast.error("Form data not loaded yet. Please wait and try again.");
      return false;
    }

    setLoading(true);
    const updatedCollection = updateCollection(latest.collectionOfPatientConsent, latest);
    const beforeDataClone   = JSON.parse(JSON.stringify(latest.beforeParsingPatientConsent));
    const result            = await formJsonRequest(updatedCollection, beforeDataClone, latest.caseFormDataId);
    setLoading(false);
    return result;
  };

  // ── signature handlers ────────────────────────────────────────────────────
  const clickPatientSign = () => setSigPadOpen(true);

  const signUpdatedCall = (base64Img) => {
    const newBase = base64Img.includes(IntakeSheetSession.dataBlock1)
      ? base64Img.replace(IntakeSheetSession.dataBlock1, IntakeSheetSession.dataBlock)
      : base64Img;
    setPatientSignImg(newBase);
    setPatientSignDate(new Date());
    sessionStorage.setItem(SHARED_SIG_KEY, newBase);
  };

  const clearSignCall = () => {
    setPatientSignImg("");
    setPatientSignDate("");
    signatureMiss?.(true);
  };

  // ── render ────────────────────────────────────────────────────────────────
  if (loading) return <SpinnerLoader message="Loading form data…" />;

  return (
    <div className="pcf__wrapper">
      <div className="pcf__title-block">
        <div className="pcf__title-main">Patient Consent Form</div>
      </div>

      <div className="pcf__consent-sections">
        <p>
          Our Notice of Privacy Practices provides information about how we may use and disclose
          protected health information about you. The Notice contains a patient rights section
          describing your rights under the law. You have the right to review our notice before
          signing this consent. The terms of our Notice may change. If we change our notice, you
          may obtain a revised copy by contacting our office.
        </p>
        <p>
          You have the right to request that we restrict how protected health information about you
          is used or disclosed for treatment, payment, or health care operations. We are not
          required to agree to this restriction, but if we do, we shall honor that agreement.
        </p>
        <p>
          By signing this form, you consent to our use and disclosure of protected health
          information about you for treatment, payment and health care operations. You have the
          right to revoke this consent, in writing signed by you. However, such revocation shall
          not affect any disclosures we have already made in reliance on your prior consent. The
          practice provides this form to comply with the Health Insurance Portability and
          Accountability Act of 1996 (HIPAA).
        </p>
        <p><b>The Patient Understands that:</b></p>
        <div className="pcf__consent-list">
          <span className="pcf__consent-list-item">1. Protected Health information may be disclosed or used for treatment, payment, or health care operations.</span>
          <span className="pcf__consent-list-item">2. The practice has a Notice of Privacy Practices and the patient has had the opportunity to review the notice.</span>
          <span className="pcf__consent-list-item">3. The practice reserves the right to change the Notice of Privacy Practices.</span>
          <span className="pcf__consent-list-item">4. The patient has the right to restrict the use of their information but the practice does not have to agree to those restrictions.</span>
          <span className="pcf__consent-list-item">5. The patient may revoke the consent in writing at any time and all future disclosures will then cease.</span>
          <span className="pcf__consent-list-item">6. The practice may condition receipt of treatment upon the execution of this consent.</span>
        </div>
      </div>

      <div className="pcf__fields">
        <div className="pcf__field-row">
          <span className="pcf__field-label"><b>This Consent was signed by:</b></span>
          <div className="pcf__field-control">
            <input
              className="pcf__input"
              type="text"
              value={patientName}
              onChange={(e) => setPatientName(e.target.value)}
            />
          </div>
        </div>

        <div className="pcf__field-row pcf__field-row--top">
          <span className="pcf__field-label"><b>Sign:</b></span>
          <div className="pcf__field-control">
            <div
              className={`pcf__sign-box${!patientSignImg ? " pcf__sign-box--empty" : ""}`}
              onClick={clickPatientSign}
            >
              {patientSignImg ? (
                <img src={patientSignImg} alt="Patient signature" className="pcf__sign-img" />
              ) : (
                <span className="pcf__sign-hint">Click to sign</span>
              )}
            </div>
          </div>
        </div>

        <div className="pcf__field-row">
          <span className="pcf__field-label"><b>Date Of Signature:</b></span>
          <div className="pcf__field-control">
            <DatePicker
              selected={patientSignDate}
              onChange={(d) => setPatientSignDate(d)}
              dateFormat="MM/dd/yyyy"
              className="pcf__datepicker"
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
            />
          </div>
        </div>

        <div className="pcf__field-row">
          <span className="pcf__field-label">
            <b>Relationship To Patient:<br />(If other than Patient)</b>
          </span>
          <div className="pcf__field-control">
            <input
              className="pcf__input"
              type="text"
              value={relationShipToPatient}
              onChange={(e) => setRelationShipToPatient(e.target.value)}
            />
          </div>
        </div>

        <div className="pcf__field-row">
          <span className="pcf__field-label"><b>Witness:</b></span>
          <div className="pcf__field-control">
            <input
              className="pcf__input"
              type="text"
              value={witness}
              onChange={(e) => setWitness(e.target.value)}
            />
          </div>
        </div>
      </div>

      <SignaturePad
        isOpen={sigPadOpen}
        title="Patient Signature"
        existingSign={patientSignImg}
        onClose={() => setSigPadOpen(false)}
        onSave={(base64) => { signUpdatedCall(base64); setSigPadOpen(false); }}
        onClear={clearSignCall}
      />
    </div>
  );
});

export default PatientConsentForm;